import HBSwiper from "@/components/HBSwiper";
import Image from "@/components/Image";
import { ImageMargin, Radius } from "@/pageComponents/MaterialCalendar/cover";
import {
  MaterialTopicCardC,
  MaterialTopicCardD,
} from "@/pageComponents/MaterialTopicCard";
import { getTopicsPreview } from "@/services";
import { getSearch } from "@/tracker/shared/global";
import { putFirst } from "@/utils/arr";
import { buildSearchParams } from "@/utils/gotoTopicSearch";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { useCreation, useSafeState, useUpdateEffect } from "ahooks";
import classNames from "classnames";
import { shuffle } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.less";

export interface RecommendTopic {
  urlname?: string;
  filter_id: string;
  preview: {
    pin_count: number;
    pins: IPin[];
  };

  origin: { title: string };

  filter: {
    name: string;
    id: number;
  };

  order: number;
  link: string;
  title?: string;
}

export interface RecommendTopicsProps {
  searchWord: string;
  titleType: "image" | "muti";
  titleImage?: string;
  iconImage?: string;
  title?: string;
  subTitle?: string;

  recommend: {
    type: "code" | "link";
    order: number;
    name: string;
    code?: string;
    recommend?: RecommendTopic[];
  }[];
}

// 随机主题封面
export function randomTopicCover(data: RecommendTopicsProps["recommend"]["0"]) {
  data?.recommend.forEach((topic) => {
    topic.preview.pins = shuffle(topic.preview.pins);
  });

  return data;
}

/**搜索推荐主题组件 */
export default function RecommendTopicList({
  searchWord,
  topics,
  recommendTopicListColNum,
  columnSize = 240,
  isFromMasonry = false,
}: {
  searchWord: string;
  topics: RecommendTopicsProps;
  recommendTopicListColNum: number;
  columnSize?: number;
  isFromMasonry?: boolean;
}) {
  const {
    titleType,
    titleImage,
    iconImage,
    title,
    subTitle = "高质量素材，商用 0 风险",
    recommend = [],
  } = topics || {};

  const swiperRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  // 使用新的通栏样式（已全量上线）
  const isNewRecommendTopicStyle = true;

  const isNewMasonryRecommendStyle = useMemo(() => {
    return isFromMasonry && isNewRecommendTopicStyle;
  }, [isFromMasonry]);

  /** 过滤没有筛选器和预览封面图片为空的, 同时将符合匹配搜索词内容提前: 相等放最前面*/
  const filterDataSource = (data: RecommendTopic[]) => {
    return putFirst(
      putFirst(
        data.filter((item) => item.filter && item.preview?.pins?.length),
        (item) => item.filter.name.includes(searchWord)
      ),
      (item) => item.filter.name === searchWord
    );
  };

  const searchTitle = title || searchWord;

  // 第一次渲染时随机封面
  useCreation(() => {
    randomTopicCover(recommend[0]);
  }, []);

  const [current, setCurrent] = useState(recommend[0]);
  const [dataSource, setDataSource] = useSafeState(
    filterDataSource(recommend[0]?.recommend || [])
  );

  const searchData = getSearch();
  // 构建跳转参数
  const searchParams = useMemo(() => {
    return buildSearchParams(searchWord, { searchData });
  }, [searchData, searchWord]);

  // 当不需要滚动时，专题列表居中对齐
  useEffect(() => {
    if (isNewRecommendTopicStyle && dataSource.length > 0) {
      const timer = setTimeout(() => {
        const swiperElement = document.querySelector(
          `.${styles.swipper}`
        ) as HTMLElement;

        if (swiperElement) {
          setNeedsScroll(swiperElement.scrollWidth > swiperElement.offsetWidth);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    dataSource,
    recommendTopicListColNum,
    columnSize,
    isNewRecommendTopicStyle,
  ]);

  // 搜索其他内容时，推荐内容更新，重置current
  useUpdateEffect(() => {
    setCurrent(randomTopicCover(recommend[0]));
  }, [recommend]);

  useUpdateEffect(() => {
    // 第一个搜索时已经默认返回了，不需要再去查询
    if (current === recommend[0]) {
      setDataSource(filterDataSource(recommend[0]?.recommend || []));
      return;
    }

    // 查询主题预览信息
    getTopicsPreview({
      preview_limit: 4,
      filter_ids: current.recommend?.map((filter) => filter.filter_id),
    }).then((res) => {
      const dataSource = filterDataSource(res.filters).map((filter) => {
        const recommend =
          current.recommend.find(
            (item) => item.filter_id === filter.filter_id
          ) || {};
        return {
          ...recommend,
          ...filter,
        };
      });

      setDataSource(dataSource);
    });
  }, [current]);

  return (
    <div
      className={classNames(styles.recommend, {
        [styles.newRecommendTopicStyle]:
          isNewRecommendTopicStyle && !isNewMasonryRecommendStyle,
        [styles.oldRecommendTopicStyle]: !isNewRecommendTopicStyle,
        [styles.newMasonryRecommendStyle]: isNewMasonryRecommendStyle,
      })}
      style={{
        width:
          columnSize * recommendTopicListColNum +
          16 * (recommendTopicListColNum - 1),
      }}
    >
      <div className={classNames(styles.header)}>
        {titleType === "image" ? (
          <Image
            {...gdImgSrcPolicy(titleImage, { displayHeight: 28 })}
            alt={searchTitle}
            height={28}
          />
        ) : (
          <div
            className={
              isNewRecommendTopicStyle && !isFromMasonry
                ? styles.center
                : styles.left
            }
          >
            {iconImage && (
              <Image
                {...gdImgSrcPolicy(iconImage, {
                  displayHeight: isNewRecommendTopicStyle ? 24 : 28,
                })}
                alt={searchTitle}
                height={isNewRecommendTopicStyle ? 24 : 28}
                {...(isNewRecommendTopicStyle && { width: 24 })}
              />
            )}
            <div className={styles.titleContainer}>
              {searchTitle && (
                <span className={styles.title}>{searchTitle}</span>
              )}
              {subTitle && <span className={styles.subTitle}>{subTitle}</span>}
            </div>
          </div>
        )}

        <div className={styles.tabs}>
          {recommend?.length > 1 &&
            recommend.map((item) => {
              return (
                <span
                  key={item.name}
                  className={classNames(styles.item, {
                    [styles.selected]: item === current,
                  })}
                  onMouseEnter={() => {
                    setCurrent(item);
                  }}
                >
                  {item.name}
                </span>
              );
            })}
        </div>
      </div>
      <div
        className={classNames(styles.content, {
          [styles.centerContent]: !needsScroll,
        })}
      >
        <HBSwiper
          ref={swiperRef}
          className={styles.swipper}
          iconClassName={styles.swiperIcon}
          contentClassName={styles.swiperContent}
        >
          {dataSource.map((item, index) => {
            return isNewRecommendTopicStyle ? (
              <MaterialTopicCardD
                title={item?.origin?.title}
                radius={Radius.outer}
                margin={ImageMargin.small}
                key={index}
                pinCount={item.preview.pin_count}
                pins={item.preview.pins}
                filter={item.filter}
                link={
                  item.urlname
                    ? `/topics/${item.urlname}-${item.filter_id}${
                        searchParams ? `?${searchParams}` : ""
                      }`
                    : item.link
                }
                data-gd-expose="resource_loc_expose"
                data-gd-click="resource_loc_click"
                data-resource-id={item.filter.id}
                data-resource-type="二级主题"
                data-resource-title={item.filter.name}
                data-content-source={`推荐主题_${current?.name}_${item.filter.name}`}
                data-resource-location={index + 1}
                data-module-name={
                  isFromMasonry ? "信息流推荐卡片" : "顶部推荐卡片"
                }
                data-subject-name="素材主题资源位"
                data-search-result-number={
                  searchData?.search_result_number || 0
                }
                data-module-location={isFromMasonry ? 1 : undefined}
              />
            ) : (
              <MaterialTopicCardC
                title={item?.origin?.title}
                radius={Radius.outer}
                margin={ImageMargin.small}
                key={index}
                pinCount={item.preview.pin_count}
                pins={item.preview.pins}
                filter={item.filter}
                link={
                  item.urlname
                    ? `/topics/${item.urlname}-${item.filter_id}`
                    : item.link
                }
                data-gd-expose="topic_expose"
                data-gd-click="topic_click"
                data-topic-id={item.filter.id}
                data-topic-type="二级主题"
                data-topic-title={item.filter.name}
                data-content-source={`推荐主题_${current?.name}_${item.filter.name}`}
              />
            );
          })}
        </HBSwiper>
      </div>
    </div>
  );
}
